import React, { ReactNode, useEffect, useState } from 'react';

interface PageMarginsProps {
  children: ReactNode;
  show?: boolean;
  showDebugInfo?: boolean;
}

/**
 * PageMargins component - Wraps the entire page content and shows margin visualization
 * This is different from Container as it's meant to be used once at the page level
 */
const PageMargins: React.FC<PageMarginsProps> = ({ 
  children, 
  show = true,
  showDebugInfo = false
}) => {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  const [showMargins, setShowMargins] = useState(show);
  const [debugVisible, setDebugVisible] = useState(showDebugInfo);

  // Update viewport width when resizing
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add keyboard shortcut to toggle margins (Alt+M)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key === 'm') {
        setShowMargins(prev => !prev);
      }
      
      if (e.altKey && e.key === 'd') {
        setDebugVisible(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Calculate margin width - consistently 10% of viewport width
  const calculateMarginWidth = () => {
    // Use the max content width of 1728px as a constraint
    const MAX_CONTENT_WIDTH = 1728;
    
    // If viewport is wider than max width, calculate based on max width
    if (viewportWidth > MAX_CONTENT_WIDTH) {
      // Return 10% of max width
      return MAX_CONTENT_WIDTH * 0.1;
    } else {
      // Otherwise use 10% of viewport width
      return viewportWidth * 0.1;
    }
  };

  // Get the margin color based on the breakpoint
  const getMarginColor = () => {
    if (viewportWidth < 640) {
      return 'rgba(168, 85, 247, 0.7)'; // Purple for mobile (360-639px)
    } else if (viewportWidth < 1024) {
      return 'rgba(250, 204, 21, 0.7)'; // Yellow for tablet (640-1023px)
    } else {
      return 'rgba(20, 184, 166, 0.7)'; // Teal for desktop (1024px+)
    }
  };

  // Render margin visualization directly in the body using a portal
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!showMargins) return;
    
    // Calculate margin width - 10% of viewport with max width constraint
    const marginWidth = calculateMarginWidth();
    const marginColor = getMarginColor();
    
    // Handle centering when viewport exceeds max width
    const MAX_CONTENT_WIDTH = 1728;
    let leftPosition = '0';
    let rightPosition = '0';
    
    if (viewportWidth > MAX_CONTENT_WIDTH) {
      // Calculate side space when centered
      const sideSpace = (viewportWidth - MAX_CONTENT_WIDTH) / 2;
      leftPosition = `${sideSpace}px`;
      rightPosition = `${sideSpace}px`;
    }

    // Create left margin element
    const leftMargin = document.createElement('div');
    leftMargin.style.position = 'fixed';
    leftMargin.style.top = '0';
    leftMargin.style.left = leftPosition;
    leftMargin.style.bottom = '0';
    leftMargin.style.width = `${marginWidth}px`;
    leftMargin.style.backgroundColor = marginColor;
    leftMargin.style.pointerEvents = 'none';
    leftMargin.style.zIndex = '2147483647'; // Maximum possible z-index
    
    // Create right margin element
    const rightMargin = document.createElement('div');
    rightMargin.style.position = 'fixed';
    rightMargin.style.top = '0';
    rightMargin.style.right = rightPosition;
    rightMargin.style.bottom = '0';
    rightMargin.style.width = `${marginWidth}px`;
    rightMargin.style.backgroundColor = marginColor;
    rightMargin.style.pointerEvents = 'none';
    rightMargin.style.zIndex = '2147483647'; // Maximum possible z-index
    
    // Add to body
    document.body.appendChild(leftMargin);
    document.body.appendChild(rightMargin);
    
    // Cleanup function
    return () => {
      document.body.removeChild(leftMargin);
      document.body.removeChild(rightMargin);
    };
  }, [showMargins, viewportWidth]);

  return (
    <>
      {/* Debug info - only shown when debugVisible is true */}
      {showMargins && debugVisible && (
        <div className="fixed top-20 left-0 right-0 z-[9999] bg-black/80 text-white p-2 text-xs flex justify-between">
          <span>Width: {viewportWidth}px {viewportWidth > 1728 ? '(Max width: 1728px)' : ''}</span>
          <span>Margin: {calculateMarginWidth().toFixed(1)}px (10%)</span>
          <span>
            {viewportWidth < 640 ? 
              'Mobile (Purple)' : 
              viewportWidth < 1024 ? 
                'Tablet (Yellow)' : 
                'Desktop (Teal)'}
          </span>
          <span>Press Alt+M to toggle margins | Alt+D for debug info</span>
        </div>
      )}
      
      <div className="relative w-full">
        {children}
      </div>
    </>
  );
};

export default PageMargins; 